/** Merge tags for My Learning completion certificates (DMS_CetificateTemplate). */

/** ascensionQ brand palette for completion certificates. */
export const ASCENSION_Q_CERT_BRAND = {
  black: "#1A1A1A",
  red: "#E3194A",
  redSoft: "#FDE8EE",
  gold: "#C9A227",
  goldLight: "#F5E6C8",
  text: "#1A1A1A",
  muted: "#64748b",
  background: "#FFFFFF",
};

/** Top-left corner accent (reference-style geometric block). */
export const ASCENSION_Q_CERT_CORNER_TL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="120" height="120" style="display:block;" aria-hidden="true">
  <polygon points="0,0 120,0 0,120" fill="${ASCENSION_Q_CERT_BRAND.black}"/>
  <polygon points="0,0 88,0 0,88" fill="${ASCENSION_Q_CERT_BRAND.red}"/>
</svg>`;

/** Bottom-right corner accent. */
export const ASCENSION_Q_CERT_CORNER_BR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="120" height="120" style="display:block;" aria-hidden="true">
  <polygon points="120,120 0,120 120,0" fill="${ASCENSION_Q_CERT_BRAND.black}"/>
  <polygon points="120,120 32,120 120,32" fill="${ASCENSION_Q_CERT_BRAND.red}"/>
</svg>`;

/** Bottom-left achievement seal with ribbons (reference layout, ascensionQ colours). */
export const ASCENSION_Q_CERT_SEAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 150" width="108" height="135" role="img" aria-label="Achievement seal">
  <defs>
    <path id="aq-seal-scallop" d="M60 6 L66 8 L72 6 L78 10 L84 8 L88 14 L94 16 L96 22 L100 28 L98 34 L100 40 L96 46 L94 52 L88 54 L84 60 L78 58 L72 62 L66 60 L60 64 L54 60 L48 62 L42 58 L36 60 L32 54 L26 52 L24 46 L20 40 L22 34 L20 28 L24 22 L26 16 L32 14 L36 8 L42 10 L48 6 L54 8 Z"/>
  </defs>
  <use href="#aq-seal-scallop" fill="${ASCENSION_Q_CERT_BRAND.gold}"/>
  <circle cx="60" cy="36" r="27" fill="${ASCENSION_Q_CERT_BRAND.black}"/>
  <circle cx="60" cy="36" r="22" fill="none" stroke="${ASCENSION_Q_CERT_BRAND.gold}" stroke-width="1.25"/>
  <text x="48" y="28" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-size="7">★</text>
  <text x="60" y="25" text-anchor="middle" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-size="8">★</text>
  <text x="72" y="28" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-size="7">★</text>
  <text x="60" y="34" text-anchor="middle" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-family="Arial,sans-serif" font-size="5.8" font-weight="700" letter-spacing="0.06em">COMMITMENT</text>
  <text x="60" y="41" text-anchor="middle" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-family="Arial,sans-serif" font-size="5.8" font-weight="700" letter-spacing="0.06em">INTEGRITY</text>
  <text x="60" y="48" text-anchor="middle" fill="${ASCENSION_Q_CERT_BRAND.gold}" font-family="Arial,sans-serif" font-size="5.8" font-weight="700" letter-spacing="0.06em">PROFESSIONALISM</text>
  <path d="M44 70 L52 96 L60 82 L68 96 L76 70 Z" fill="${ASCENSION_Q_CERT_BRAND.red}"/>
  <path d="M52 96 L48 116 L56 102 Z" fill="${ASCENSION_Q_CERT_BRAND.red}"/>
  <path d="M68 96 L64 116 L72 102 Z" fill="${ASCENSION_Q_CERT_BRAND.red}"/>
</svg>`;

export const QMS_CERTIFICATE_TEMPLATE_MERGE_TAGS = [
  { tag: "{StaffName}", entityName: "StaffName", description: "Staff name" },
  { tag: "{DocumentTitle}", entityName: "DocumentTitle", description: "Document title" },
  { tag: "{CompletionDate}", entityName: "CompletionDate", description: "Completion date and time" },
  { tag: "{{DocumentCode}}", entityName: "DocumentCode", description: "Document type/category code" },
  { tag: "{{VersionNumber}}", entityName: "VersionNumber", description: "Document version number" },
  {
    tag: "{{OrganizationName}}",
    entityName: "OrganizationName",
    description: "Organisation display name",
  },
];

export const QMS_CERTIFICATE_TEMPLATE_SAMPLE_CONTEXT = {
  staffName: "Alex Morgan",
  documentTitle: "SOP-001 Training Guide.pdf",
  documentCode: "SOP_CLI_00007_V1",
  completedOn: "22/06/2026 11:32 AM",
  versionNumber: 1,
  organizationName: "Acme Clinical Trials",
};

/** Inner certificate copy only — frame, corners, and award icon are fixed in React. */
export const QMS_DEFAULT_CERTIFICATE_BODY_HTML = `
<h1 class="qms-cert-default__heading">CERTIFICATE</h1>
<div class="qms-cert-default__subtitle-row">
  <span class="qms-cert-default__subtitle-line"></span>
  <p class="qms-cert-default__subtitle">OF COMPLETION</p>
  <span class="qms-cert-default__subtitle-line"></span>
</div>
<p class="qms-cert-default__eyebrow">This is to certify that</p>
<p class="qms-cert-default__name">{StaffName}</p>
<div class="qms-cert-default__divider"></div>
<p class="qms-cert-default__body-text">has successfully completed the required learning for <strong class="qms-cert-default__body-strong">{DocumentTitle}</strong> (<span class="qms-cert-default__body-accent">{{DocumentCode}}</span>) on <strong class="qms-cert-default__body-accent">{CompletionDate}</strong>.</p>
<p class="qms-cert-default__version">Version <strong>{{VersionNumber}}</strong></p>
<div class="qms-cert-default__issued"><span>Issued by</span><p class="qms-cert-default__org">{{OrganizationName}}</p></div>
`.trim();

/** Legacy full-page HTML (config editor / old saves); body is extracted at render time. */
export const QMS_DEFAULT_CERTIFICATE_TEMPLATE_HTML = `
<div class="qms-cert-default">
  <div class="qms-cert-default__frame">
    <div class="qms-cert-default__inner">
      <div class="qms-cert-default__content">
        <div class="qms-cert-default__award" aria-hidden="true"><i class="fas fa-award"></i></div>
        ${QMS_DEFAULT_CERTIFICATE_BODY_HTML}
      </div>
    </div>
  </div>
</div>
`.trim();

/**
 * Pull editable body HTML from a saved template (strips fixed shell, corners, award).
 */
export function extractCertificateTemplateBody(template) {
  const raw = String(template || "").trim();
  if (!raw) return QMS_DEFAULT_CERTIFICATE_BODY_HTML;

  const stripFixedChrome = (root) => {
    if (!root) return "";
    root.querySelectorAll(".qms-cert-default__award").forEach((el) => el.remove());
    root.querySelectorAll(".qms-cert-default__corner").forEach((el) => el.remove());
    return root.innerHTML.trim();
  };

  if (typeof document !== "undefined") {
    const wrap = document.createElement("div");
    wrap.innerHTML = raw;

    const content = wrap.querySelector(".qms-cert-default__content");
    if (content) return stripFixedChrome(content);

    const inner = wrap.querySelector(".qms-cert-default__inner");
    if (inner) return stripFixedChrome(inner);

    if (wrap.querySelector(".qms-cert-default")) {
      return stripFixedChrome(wrap);
    }

    return raw;
  }

  if (!/qms-cert-default/i.test(raw)) return raw;

  const contentMatch = raw.match(
    /class=["'][^"']*qms-cert-default__content[^"']*["'][^>]*>([\s\S]*?)<\/div>\s*<\/div>/i,
  );
  if (contentMatch?.[1]) {
    return contentMatch[1]
      .replace(/<div[^>]*qms-cert-default__award[\s\S]*?<\/div>/gi, "")
      .trim();
  }

  return raw;
}

export function resolveCertificateTemplateContent(content) {
  const raw = String(content || "").trim();
  if (!raw || isLegacyCertificateTemplate(raw)) {
    return QMS_DEFAULT_CERTIFICATE_BODY_HTML;
  }
  return extractCertificateTemplateBody(raw);
}

/** Opt-in marker for organisations that supply a fully custom HTML certificate layout. */
export const QMS_CUSTOM_CERTIFICATE_LAYOUT_MARKER = 'data-qms-certificate-layout="custom"';

export function shouldUseCustomCertificateHtml(content) {
  const raw = String(content || "").trim();
  if (!raw) return false;
  return raw.includes(QMS_CUSTOM_CERTIFICATE_LAYOUT_MARKER);
}

/** Ancient inline-styled templates with no merge tags (pre–QMS Config). */
export function isLegacyCertificateTemplate(content) {
  const raw = String(content || "").trim();
  if (!raw) return true;

  if (raw.includes("#92702a")) return true;

  // Keep classic "Certificate of Completion" org templates (incl. NHS-style layouts).
  if (
    raw.includes("Certificate of Completion") ||
    /this is certify that/i.test(raw) ||
    /this is to certify that/i.test(raw)
  ) {
    return false;
  }

  if (
    !raw.includes("OF COMPLETION") &&
    !raw.includes("{StaffName}") &&
    !raw.includes("{{StaffName}}")
  ) {
    return true;
  }

  return false;
}

/**
 * Completion certificates: fixed frame + award; body HTML from org template.
 * Full custom HTML only when data-qms-certificate-layout="custom" is set.
 */
export function shouldUseBuiltInCertificateTemplate(content) {
  return !shouldUseCustomCertificateHtml(content);
}

export function hasSavedCertificateTemplate(content) {
  const raw = String(content || "").trim();
  return Boolean(raw) && !isLegacyCertificateTemplate(raw);
}
